import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { de, enAU as en } from 'date-fns/locale';
import Cookies from 'js-cookie';
import { GERMANY, SUPPORTED_LANGUAGES } from './constants/constants';
import { enUiVisibleTexts } from './translation/en';
import { deUiVisibleTexts } from './translation/de';
import { dynamicWords } from './translation/dynamicWords';
import { getDeployRegion } from './utils/configurationHelpers';

let baseResources = {
  en: {
    translation: { ...enUiVisibleTexts.en, ...dynamicWords.en },
  },
  de: {
    translation: { ...deUiVisibleTexts.de, ...dynamicWords.de },
  },
};
const REGION_OVERRIDES = {
  [GERMANY]: {
    en: {
      Patient: 'Client',
      Comments_for_practitioners_and_reviewers__This_will_not_appear_on_the_patient_s_exam_report:
        'Comments for practitioners and reviewers. This will not appear on the client’s exam report',
      Patient_Account_No_: 'Client Account No.',
      No_matching_patients_records_found_: 'No matching clients records found.',
      Exam_Report_provided_to_patient_by_Health_Care_Professional:
        'Exam Report provided to client by Health Care Professional',
      The_DR_Grading_software_is_intended:
        'The DR Grading software is intended to screen and grade Diabetic Retinopathy (DR) in clients/general population through colour fundus retinal images. The DR Grader is indicated for use by health care providers (HCP) to automatically detect and grade diabetic retinopathy in clients who have been diagnosed with diabetes and screen those who have not yet been diagnosed with diabetes.',
      Patient_Type: 'Client Type',
      Please_Select_Patient_Type: 'Please select client Type',
      Send_the_report_to_the_patient_through_the_chosen_method:
        'Send the report to the client through the chosen method',
      Allow_patient_to_access_this_report_through_the_mobile_app:
        'Allow client to access this report through the mobile app',
      Grant_patient_access: 'Grant client access',
      There_was_a_problem_granting_patient_access__Please_try_again_:
        'There was a problem granting client access. Please try again.',
      Upload_patient_s_audiogram__Accepted_formats__PDF__Max_file_size_3MB_:
        'Upload client’s audiogram. Accepted formats: PDF(Max file size 3MB)',
      There_was_a_problem_retrieving_patients__Please_try_again_:
        'There was a problem retrieving clients. Please try again.',
      No_patients_found_: 'No clients found.',
      No_matching_patient_records_found_: 'No matching client records found.',
      There_was_a_problem_retrieving_patient_details__Please_refresh_the_page_to_try_again_:
        'There was a problem retrieving client details. Please refresh the page to try again.',
      Successfully_updated_patient_details_: 'Successfully updated client details.',
      There_is_a_problem_when_updating_patient_details__Please_try_again_later_:
        'There is a problem when updating client details. Please try again later.',
      There_was_a_problem_when_retrieving_patient_s_exams__Please_refresh_the_page_to_try_again_:
        "There was a problem when retrieving client's exams. Please refresh the page to try again.",
      There_was_a_problem_creating_a_new_patient__Please_try_again_later_:
        'There was a problem creating a new client. Please try again later.',
      Patient_s_eyes_bleeding__Image_blurry__Let_the_eye_reviewer_know_by_adding_a_comment_:
        "Client's eyes bleeding? Image blurry? Let the eye reviewer know by adding a comment!",
      Patient_LookUp: 'Client LookUp',
      This_is_the_patient_s: "This is the client's",
      All_Patients: 'All Clients',
      Register_Patient: 'Register Client',
      Not_Applicable_tooltip: 'Not applicable for clients under the age of 45',
      RetinoScan_analyses_retinal_images_to_accurately_identify_patients__with_Diabetic_Retinopathy__Glaucoma_and_or_Age_Related_Macular_Degeneration_:
        "RetinoScan analyses retinal images to accurately identify patients' with Diabetic Retinopathy, Glaucoma and/or Age Related Macular Degeneration.",
      For_patients_with_a_disease_diagnosis__RetinoScan_Advanced_determines_the_disease_severity_and_provide_lesion_monitoring_services_:
        'For clients with a disease diagnosis, RetinoScan Advanced determines the disease severity and provide lesion monitoring services.',
      Comprehensive_Patient_Report: 'Comprehensive Client Report',
      Patient_Data_Retention: 'Client Data Retention',
      In_accordance_to_HIPPA_compliances__all_patients__data_will_be_stored_and_backed_up_for_15_years:
        "In accordance to HIPPA compliances, all clients' data will be stored and backed up for 15 years",
      Patient_s_account_number_is_unique_and_not_available_for_modification_:
        "Client's account number is unique and not available for modification.",
      If_this_case_is_URGENT__we_recommend_you_to_contact_the_patient_immediately_:
        'If this case is URGENT, we recommend you to contact the client immediately.',
      Can_be_viewed_only_by_the_patient_s: 'Can be viewed only by the client’s',
      Can_be_viewed_by_both_the_patient_and_practitioners_within_TeleEye_MD_and_the_report:
        'Can be viewed by both the client and practitioners within TeleEye MD and the report',
      Patient_s_Profile: "Client's Profile",
      Send_a_report_link_to_the_patient_through_the_chosen_method_:
        'Send a report link to the client through the chosen method.',
      UPLOAD_IMAGES_OR_WAIT_FOR_PATIENT_TO_VISIT_OPTICAL_STORE:
        'UPLOAD IMAGES OR WAIT FOR CLIENT TO VISIT OPTICAL STORE',
      Enter_account_number_to_retrieve_patient_: 'Enter account number to retrieve client.',
      Total_Patients: 'Total clients',
      The_patient_did_not_leave_a_note: 'The client did not leave a note',
      Patient_Name: 'Client Name',
      The_patient_doesn_t_have_any_history_meetings: "The client doesn't have any history meetings",
      New_Patient_Creation: 'New Client Creation',
      This_patient_does_not_have_any_existing_exams_: 'This client does not have any existing exams.',
      Each_patient_can_only_have_a_maximum_of_50_exams_: 'Each client can only have a maximum of 50 exams.',
      Each_patient_can_only_have_a_maximum_of_50_records_: 'Each client can only have a maximum of 50 records.',
      Enter_name_s__and_or_date_of_birth_to_retrieve_patient_: 'Enter name(s) and/or date of birth to retrieve client.',
      Please_enter_some_patient_information_to_search: 'Please enter some client information to search',
      Your_organisation_has_yet_to_register_any_patients_: 'Your organisation has yet to register any clients.',
      Create_Patient: 'Create Client',
      Total_Patients_Screened: 'Total Clients Screened',
      Patient_Details: 'Client Details',
    },
    de: {
      Patient: 'Kunde',
      Comments_for_practitioners_and_reviewers__This_will_not_appear_on_the_patient_s_exam_report:
        'Kommentare für Untersucher*innen und Überprüfer*innen Diese erscheinen nicht im Prüfungsbericht des Kunden.',
      Patient_Account_No_: 'Kundenkontonummer',
      No_matching_patients_records_found_: 'Es wurden keine passenden Kundendatensätze gefunden.',
      Exam_Report_provided_to_patient_by_Health_Care_Professional:
        'Untersuchungsbericht, der dem Kunden von einem medizinischen Fachpersonal gestellt wird',
      The_DR_Grading_software_is_intended:
        'Die DR-Grading-Software dient zur Untersuchung und Einstufung der diabetischen Retinopathie (DR) bei Patienten/der Allgemeinbevölkerung anhand von Farbfotos des Augenhintergrunds. Der DR Grader ist für die Verwendung durch medizinisches Fachpersonal vorgesehen, um bei Patienten, bei denen Diabetes diagnostiziert wurde, automatisch eine diabetische Retinopathie zu erkennen und einzustufen und um Patienten zu untersuchen, bei denen noch keine Diabetesdiagnose vorliegt.',

      Patient_Type: 'Kundentyp',
      Please_Select_Patient_Type: 'Bitte wählen Sie den Kundentyp aus.',
      Send_the_report_to_the_patient_through_the_chosen_method:
        'Senden Sie den Bericht über die gewählte Methode an den Kunden.',
      Allow_patient_to_access_this_report_through_the_mobile_app:
        'Dem Kunden den Zugriff auf diesen Bericht über die App ermöglichen',
      Grant_patient_access: 'Kundenzugang gewähren',
      There_was_a_problem_granting_patient_access__Please_try_again_:
        'Es gab ein Problem beim Gewähren des Kundenzugriffs. Bitte versuchen Sie es erneut.',
      Upload_patient_s_audiogram__Accepted_formats__PDF__Max_file_size_3MB_:
        'Laden Sie das Audiogramm des Kunden hoch. Akzeptierte Formate: PDF (maximale Dateigröße 3 MB)',
      There_was_a_problem_retrieving_patients__Please_try_again_:
        'Es gab ein Problem beim Abrufen des Kunden Bitte versuchen Sie es erneut.',
      No_patients_found_: 'Keine Kunden gefunden.',
      No_matching_patient_records_found_: 'Es wurden keine passenden Kundendatensätze gefunden.',
      There_was_a_problem_retrieving_patient_details__Please_refresh_the_page_to_try_again_:
        'Beim Abrufen der Kundendaten ist ein Problem aufgetreten. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
      Successfully_updated_patient_details_: 'Kundendaten erfolgreich aktualisiert.',
      There_is_a_problem_when_updating_patient_details__Please_try_again_later_:
        'Es gibt ein Problem bei der Aktualisierung der Kundendaten. Bitte versuchen Sie es später erneut.',
      There_was_a_problem_when_retrieving_patient_s_exams__Please_refresh_the_page_to_try_again_:
        'Beim Abrufen der Prüfungsergebnisse des Kunden ist ein Problem aufgetreten. Bitte aktualisieren Sie die Seite, um es erneut zu versuchen.',
      There_was_a_problem_creating_a_new_patient__Please_try_again_later_:
        'Es gab ein Problem beim Erstellen eines neuen Kunden. Bitte versuchen Sie es später erneut.',
      Patient_s_eyes_bleeding__Image_blurry__Let_the_eye_reviewer_know_by_adding_a_comment_:
        'Gibt es besondere Auffälligkeiten beim Auge des Kunden? Ist das Bild unscharf? Informieren Sie den Überprüfer, indem Sie einen Kommentar hinzufügen!',
      Patient_LookUp: 'Kundensuche',
      This_is_the_patient_s: 'Das ist der Kunde.',
      All_Patients: 'Alle Kunden',
      Register_Patient: 'Kunden registrieren',
      Not_Applicable_tooltip: 'Nicht anwendbar für Kunden unter 45 Jahren',
      RetinoScan_analyses_retinal_images_to_accurately_identify_patients__with_Diabetic_Retinopathy__Glaucoma_and_or_Age_Related_Macular_Degeneration_:
        'RetinoScan analysiert Netzhautbilder, um Kunden mit diabetischer Retinopathie, Glaukom und/oder altersbedingter Makuladegeneration zu identifizieren.',
      For_patients_with_a_disease_diagnosis__RetinoScan_Advanced_determines_the_disease_severity_and_provide_lesion_monitoring_services_:
        'Bei Kunden mit einer Krankheitsdiagnose bestimmt RetinoScan Advanced den Schweregrad der Erkrankung und bietet Dienste zur Überwachung der Läsionen an.',
      Comprehensive_Patient_Report: 'Umfassender Kundenbericht',
      Patient_Data_Retention: 'Aufbewahrung von Kundendaten',
      In_accordance_to_HIPPA_compliances__all_patients__data_will_be_stored_and_backed_up_for_15_years:
        'In Übereinstimmung mit den HIPPA-Vorschriften werden alle Kundendaten 15 Jahre lang gespeichert und gesichert.',
      Patient_s_account_number_is_unique_and_not_available_for_modification_:
        'Die Kontonummer des Kunden ist eindeutig und kann nicht geändert werden.',
      If_this_case_is_URGENT__we_recommend_you_to_contact_the_patient_immediately_:
        'Wenn dieser Fall DRINGEND ist, empfehlen wir Ihnen, sich umgehend mit dem Kunden in Verbindung zu setzen.',
      Can_be_viewed_only_by_the_patient_s: 'Kann nur vom Kunden eingesehen werden.',
      Can_be_viewed_by_both_the_patient_and_practitioners_within_TeleEye_MD_and_the_report:
        'Kann sowohl vom Kunden als auch von den Ärzten innerhalb von TeleEye MD und dem Bericht eingesehen werden.',
      Patient_s_Profile: 'Kundenprofil',
      Send_a_report_link_to_the_patient_through_the_chosen_method_:
        'Senden Sie dem Kunden einen Link für den Bericht über die gewählte Methode.',
      UPLOAD_IMAGES_OR_WAIT_FOR_PATIENT_TO_VISIT_OPTICAL_STORE:
        'Bilder hochladen oder warten, bis der Kunde das Optikergeschäft besucht',
      Enter_account_number_to_retrieve_patient_: 'Geben Sie die Kontonummer ein, um den Kunden abzurufen.',
      Total_Patients: 'Gesamtzahl der Kunden',
      The_patient_did_not_leave_a_note: 'Der Kunde hat keine Nachricht hinterlassen.',
      Patient_Name: 'Kundenname',
      The_patient_doesn_t_have_any_history_meetings: 'Der Kunde hat keine bisherigen Untersuchungen.',
      New_Patient_Creation: 'Kundenregistrierung',
      This_patient_does_not_have_any_existing_exams_: 'Dieser Kunde hat keine bestehenden Überprüfungen.',
      Each_patient_can_only_have_a_maximum_of_50_exams_: 'Jeder Kunde kann maximal 50 Überprüfungen absolvieren.',
      Each_patient_can_only_have_a_maximum_of_50_records_: 'Jeder Kunde kann maximal 50 Screenings haben.',
      Enter_name_s__and_or_date_of_birth_to_retrieve_patient_:
        'Geben Sie den/die Namen und/oder das Geburtsdatum ein, um den Kunden zu finden.',
      Please_enter_some_patient_information_to_search:
        'Bitte geben Sie die Kundeninformationen ein, um die Suche zu starten.',
      Your_organisation_has_yet_to_register_any_patients_: 'Ihre Organisation hat noch keine Kunden registriert.',
      Create_Patient: 'Kunden anlegen',
      Total_Patients_Screened: 'Gesamtzahl der untersuchten Kunden',
      Patient_Details: 'Kundendaten',
    },
  },
};
function getResources(region) {
  const overrides = REGION_OVERRIDES[region] || {};
  return {
    en: {
      translation: { ...enUiVisibleTexts.en, ...dynamicWords.en, ...(overrides.en || {}) },
    },
    de: {
      translation: { ...deUiVisibleTexts.de, ...dynamicWords.de, ...(overrides.de || {}) },
    },
  };
}
function getSupportedLanguage() {
  let cookieLang = Cookies.get('django_language');

  const browserLang = window.navigator.language;
  const localStorageLang = window.localStorage.i18nextLng;

  const language = cookieLang || localStorageLang || browserLang || 'en';

  const baseLanguage = language.split('-')[0];

  return SUPPORTED_LANGUAGES.includes(baseLanguage) ? baseLanguage : 'en';
}
const resources = getResources(getDeployRegion());

const initialLang = getSupportedLanguage();
const availableLanguages = ['en', 'de'];

const i18nConfig = {
  fallbackLng: 'en',
  lng: initialLang,
  resources,
  detection: {
    // Define detection order and methods
    order: ['cookie', 'localStorage', 'sessionStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
    caches: ['localStorage'],
    lookupCookie: 'django_language', // follow django language
    excludeCacheFor: ['cimode'],
    cookieMinutes: 10,
    cookieDomain: 'myDomain',
    checkWhitelist: true,
  },
  whitelist: availableLanguages,
  react: {
    useSuspense: false,
  },
  interpolation: {
    escapeValue: false,
  },
};

i18n.use(initReactI18next).use(LanguageDetector).init(i18nConfig);

export const datePickerLangText = (argLang) => {
  switch (argLang) {
    case 'en': {
      return en;
    }
    case 'de': {
      return de;
    }
    default: {
      return en;
    }
  }
};
